// Video notes (like WhatsApp's round video messages): up to 60 seconds from
// the front camera, sent end-to-end encrypted as a video marked "note".
import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { prepareFile } from "../media";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

export const recordingNote = signal<string | null>(null); // the chat it's for
const MAX_S = 60;

export function VideoNoteRecorder({ jid }: { jid: string }) {
  const video = useRef<HTMLVideoElement>(null);
  const [secs, setSecs] = useState(0);
  const [error, setError] = useState("");
  const rec = useRef<{ stream: MediaStream; recorder: MediaRecorder; chunks: Blob[]; started: number } | null>(null);
  const close = () => (recordingNote.value = null);

  useEffect(() => {
    let stopped = false;
    void navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 }, aspectRatio: { ideal: 1 } }, audio: true })
      .then((stream) => {
        if (stopped) return stream.getTracks().forEach((t) => t.stop());
        video.current!.srcObject = stream;
        void video.current!.play().catch(() => {});
        const type = ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
        const recorder = new MediaRecorder(stream, type ? { mimeType: type, videoBitsPerSecond: 800_000 } : undefined);
        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        recorder.start(250);
        rec.current = { stream, recorder, chunks, started: Date.now() };
      })
      .catch((e) => setError((e as DOMException).name === "NotAllowedError" ? "Allow camera and microphone access to record a video note" : (e as Error).message));
    const t = setInterval(() => {
      const r = rec.current;
      if (!r) return;
      const s = Math.floor((Date.now() - r.started) / 1000);
      setSecs(s);
      if (s >= MAX_S) void finish(true);
    }, 250);
    return () => {
      stopped = true;
      clearInterval(t);
      rec.current?.stream.getTracks().forEach((x) => x.stop());
    };
  }, []);

  async function finish(send: boolean) {
    const r = rec.current;
    rec.current = null;
    close();
    if (!r) return;
    const done = new Promise((res) => (r.recorder.onstop = res));
    r.recorder.stop();
    await done;
    r.stream.getTracks().forEach((x) => x.stop());
    if (!send || !r.chunks.length) return;
    try {
      const type = r.recorder.mimeType || "video/webm";
      const file = new File(r.chunks, "note." + (type.includes("mp4") ? "mp4" : "webm"), { type: type.split(";")[0] });
      const p = await prepareFile(file);
      await engine.value?.sendDraft(jid, { body: "", kind: "video", media: { ...p.media, note: true, name: undefined, duration: p.media.duration ?? (Date.now() - r.started) / 1000 } });
    } catch (e) {
      toast("Couldn't send the video note: " + (e as Error).message);
    }
  }

  return (
    <div class="modal-backdrop note-backdrop">
      <div class="note-recorder" role="dialog" aria-label="Record a video note">
        {error ? (
          <p class="error">{error}</p>
        ) : (
          <div class="note-circle">
            <video ref={video} muted playsInline />
            <svg viewBox="0 0 100 100" class="note-progress" aria-hidden="true">
              <circle cx="50" cy="50" r="48" pathLength={MAX_S} style={{ strokeDasharray: `${secs} ${MAX_S}` }} />
            </svg>
          </div>
        )}
        <div class="note-controls">
          <button class="icon-btn" onClick={() => void finish(false)} aria-label="Cancel video note">
            <Icon name="trash" />
          </button>
          <span class="note-time">
            <span class="rec-dot" /> 0:{String(secs).padStart(2, "0")}
          </span>
          <button class="icon-btn send-fab" disabled={!!error} onClick={() => void finish(true)} aria-label="Send video note">
            <Icon name="send" />
          </button>
        </div>
      </div>
    </div>
  );
}
